import React, { useState, useRef, useCallback } from 'react';
import { _T, C } from '../../../shared/theme';
import { s_PB, s_GC } from '../../../shared/styles';
import { saveGameScore } from '../../../shared/utils';

const TONES = [
  { id: 0, freq: 261, color: '#E05252', label: 'C' },
  { id: 1, freq: 329, color: '#5B9ED0', label: 'E' },
  { id: 2, freq: 392, color: '#4AC87A', label: 'G' },
  { id: 3, freq: 523, color: '#C8A84B', label: 'C2' },
];

function playToneId(id, duration = 0.35) {
  const { freq } = TONES[id];
  try {
    const ac = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ac.createOscillator();
    const gain = ac.createGain();
    osc.type = 'sine'; osc.frequency.value = freq;
    gain.gain.setValueAtTime(0.4, ac.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ac.currentTime + duration);
    osc.connect(gain); gain.connect(ac.destination);
    osc.start(); osc.stop(ac.currentTime + duration);
    setTimeout(() => ac.close(), (duration + 0.1) * 1000);
  } catch {}
}

function SoundMemoryGame({ lang, onBack }) {
  const nl = lang === 'nl';
  const [sequence, setSequence] = useState([]);
  const [phase, setPhase] = useState('start'); // start | showing | input | result | done
  const [userInput, setUserInput] = useState([]);
  const [activeBtn, setActiveBtn] = useState(null);
  const [lives, setLives] = useState(3);
  const [level, setLevel] = useState(1);
  const [maxLevel, setMaxLevel] = useState(0);
  const timerRef = useRef(null);

  const playSequence = useCallback((seq) => {
    setPhase('showing');
    seq.forEach((toneId, i) => {
      timerRef.current = setTimeout(() => {
        setActiveBtn(toneId);
        playToneId(toneId, 0.3);
        setTimeout(() => setActiveBtn(null), 350);
        if (i === seq.length - 1) {
          setTimeout(() => setPhase('input'), 500);
        }
      }, i * 700);
    });
  }, []);

  const startLevel = useCallback((lvl, prevSeq) => {
    const newSeq = [...prevSeq, Math.floor(Math.random() * 4)];
    setSequence(newSeq); setUserInput([]);
    setTimeout(() => playSequence(newSeq), 400);
  }, [playSequence]);

  const startGame = () => {
    setLives(3); setLevel(1); setMaxLevel(0); setSequence([]); setUserInput([]);
    startLevel(1, []);
  };

  const handleTap = (toneId) => {
    if (phase !== 'input') return;
    playToneId(toneId, 0.25);
    setActiveBtn(toneId);
    setTimeout(() => setActiveBtn(null), 200);
    const next = [...userInput, toneId];
    setUserInput(next);

    const idx = next.length - 1;
    if (next[idx] !== sequence[idx]) {
      // Wrong
      const newLives = lives - 1;
      setLives(newLives); setPhase('result');
      playToneId(0, 0.5);
      if (newLives <= 0) {
        setTimeout(() => { saveGameScore('soundmemory', maxLevel); setPhase('done'); }, 900);
      } else {
        setTimeout(() => {
          const prev = sequence.slice(0, -1);
          startLevel(level, prev);
        }, 1000);
      }
      return;
    }

    if (next.length === sequence.length) {
      // Correct round
      setPhase('result');
      playToneId(3, 0.4);
      const nextLvl = level + 1;
      setLevel(nextLvl);
      if (nextLvl - 1 > maxLevel) setMaxLevel(nextLvl - 1);
      setTimeout(() => startLevel(nextLvl, sequence), 900);
    }
  };

  if (phase === 'done') {
    return (
      <div>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: C.silver, cursor: 'pointer', fontSize: 13, marginBottom: 12 }}>← {nl ? 'Terug' : 'Back'}</button>
        <div style={{ ...s_GC({ padding: 24, textAlign: 'center' }) }}>
          <div style={{ fontSize: 52, marginBottom: 12 }}>🎵</div>
          <div style={{ color: C.white, fontSize: 20, fontWeight: 700, marginBottom: 4 }}>{nl ? 'Spel voorbij!' : 'Game over!'}</div>
          <div style={{ color: _T.accent, fontSize: 32, fontWeight: 700 }}>{nl ? `Niveau ${maxLevel}` : `Level ${maxLevel}`}</div>
          <div style={{ color: C.silver, fontSize: 13, marginBottom: 16 }}>{nl ? 'maximaal bereikt' : 'reached'}</div>
          <button style={s_PB({ width: '100%' })} onClick={startGame}>{nl ? 'Opnieuw' : 'Play again'}</button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <button onClick={onBack} style={{ background: 'none', border: 'none', color: C.silver, cursor: 'pointer', fontSize: 13, marginBottom: 12 }}>← {nl ? 'Terug' : 'Back'}</button>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
        <span style={{ color: C.silver, fontSize: 12 }}>{nl ? `Niveau ${level}` : `Level ${level}`} — {Array.from({ length: 3 }, (_, i) => i < lives ? '❤️' : '🖤').join('')}</span>
        <span style={{ color: _T.accent, fontSize: 12 }}>{nl ? `Reeks: ${sequence.length}` : `Sequence: ${sequence.length}`}</span>
      </div>

      <div style={{ ...s_GC({ padding: 16, textAlign: 'center', marginBottom: 14 }), minHeight: 60 }}>
        {phase === 'start' && <div style={{ color: C.silver, fontSize: 13, lineHeight: 1.5 }}>{nl ? 'Luister naar de reeks en herhaal hem door op de knoppen te tikken.' : 'Listen to the sequence and repeat it by tapping the buttons.'}</div>}
        {phase === 'showing' && <div style={{ color: C.orange, fontWeight: 700 }}>{nl ? '👂 Luister...' : '👂 Listen...'}</div>}
        {phase === 'input' && <div style={{ color: C.teal, fontWeight: 700 }}>{nl ? `🎯 Herhaal! ${userInput.length}/${sequence.length}` : `🎯 Repeat! ${userInput.length}/${sequence.length}`}</div>}
        {phase === 'result' && <div style={{ color: userInput[userInput.length - 1] === sequence[userInput.length - 1] ? C.teal : C.red, fontWeight: 700 }}>{userInput[userInput.length - 1] === sequence[userInput.length - 1] ? (nl ? '✅ Goed!' : '✅ Correct!') : (nl ? '❌ Fout!' : '❌ Wrong!')}</div>}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        {TONES.map(t => (
          <button key={t.id} onClick={() => handleTap(t.id)} disabled={phase === 'showing' || phase === 'start'}
            style={{ height: 90, borderRadius: 18, border: 'none', cursor: phase === 'input' ? 'pointer' : 'default', fontSize: 22, fontWeight: 700, color: '#0a1220', background: activeBtn === t.id ? t.color : `${t.color}44`, transition: 'background 0.1s, transform 0.1s', transform: activeBtn === t.id ? 'scale(1.05)' : 'scale(1)', boxShadow: activeBtn === t.id ? `0 0 20px ${t.color}88` : 'none' }}>
            {t.label}
          </button>
        ))}
      </div>

      {phase === 'start' && (
        <button style={s_PB({ width: '100%', marginTop: 16 })} onClick={startGame}>{nl ? 'Start' : 'Start'}</button>
      )}
    </div>
  );
}

export default SoundMemoryGame;
